import { API_SERVICE } from "@/services/api.service";

export default {
    namespaced: true,
    state: {
        contractor: '',
        contacts: [],
        maintenanceJobs: [],
        historicalStatements: [],
        currentHistoricalStatement: null,
        pendingStatement: null,
        submittedInvoices: []
    },
    mutations: {
        SET_CONTRACTOR(state, data) {
            state.contractor = data;
        },
        SET_CONTACTS(state, data) {
            state.contacts = data;
        },
        SET_MAINTENANCE_JOBS(state, data) {
            state.maintenanceJobs = data;
        },
        SET_HISTORICAL_STATEMENTS(state, data) {
            state.historicalStatements = data;
        },
        SET_CURRENT_HISTORICAL_STATEMENT(state, data) {
            state.currentHistoricalStatement = data;
        },
        SET_PENDING_STATEMENT(state, data) {
            state.pendingStatement = data;
        },
        SET_SUBMITTED_INVOICES(state, data) {
            state.submittedInvoices = data;
        },
        SET_MAINTENANCE_JOB_NOTES(state, { jobID, notes }) {
            // Find the job and update its notes
            const jobIndex = state.maintenanceJobs.findIndex(job => job.OID === jobID);
            if (jobIndex !== -1) {
                // Using Vue.set or direct property assignment based on your Vue version
                state.maintenanceJobs[jobIndex].notes = notes;
            }
        }
    },
    getters: {
        contractor(state) {
            return state.contractor;
        },
        contacts(state) {
            return state.contacts;
        },
        maintenanceJobs(state) {
            return state.maintenanceJobs;
        },
        historicalStatements(state) {
            return state.historicalStatements;
        },
        currentHistoricalStatement(state) {
            return state.currentHistoricalStatement;
        },
        pendingStatement(state) {
            return state.pendingStatement;
        },
        submittedInvoices(state) {
            return state.submittedInvoices;
        },
        name(state) {
            if (!state.contractor) {
                return '';
            }
            return state.contractor.Name || '';
        },
        balance(state) {
            if (!state.contractor) {
                return 0;
            }
            return state.contractor.AccountBalance || 0;
        },
        maintenanceJobNotes: (state) => (jobID) => {
            const job = state.maintenanceJobs.find(job => job.OID === jobID);
            return job && job.notes ? job.notes : [];
        },

    },
    actions: {
        // Get contractor maintenance job notes
        async getMaintenanceJobNotes({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    LandlordID: payload.contractorID,  // Passing contractorID as LandlordID for API compatibility
                    jobID: payload.jobID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/landlord/maintenance-job-notes", params).then((res) => {
                if (res.status === "200") {
                    // Add notes to the appropriate jobs in the state
                    commit("SET_MAINTENANCE_JOB_NOTES", { 
                        jobID: payload.jobID, 
                        notes: res.data 
                    });
                    return res.data;
                }
                return [];
            }).catch((error) => {
                console.error("Error getting contractor maintenance job notes:", error);
                return [];
            });
        },
        getContractor({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_CONTRACTOR", res.data);
                    return res.data;
                }
                return null;
            });
        },
        
        getContacts({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/contacts", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_CONTACTS", res.data);
                    return res.data;
                }
                return [];
            });
        },
        
        getMaintenanceJobs({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/maintenance-jobs", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_MAINTENANCE_JOBS", res.data);
                    return res.data;
                }
                return [];
            });
        },
        
        toggleJobComplete({ dispatch }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID,
                    jobOID: payload.jobOID
                }
            };
            return new Promise((resolve) => {
                API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/maintenance-job/toggle-complete", params).then((res) => {
                    // Refresh the jobs list after toggling completion
                    dispatch("getMaintenanceJobs", { 
                        clientName: payload.clientName,
                        contractorID: payload.contractorID
                    });
                    
                    if (res.status === "200") {
                        resolve({
                            success: true,
                            message: "Job status updated successfully"
                        });
                    } else {
                        resolve({
                            success: false,
                            message: "Failed to update job status"
                        });
                    }
                }).catch(() => {
                    resolve({
                        success: false,
                        message: "Failed to update job status"
                    });
                });
            });
        },
        
        addJobNote({ dispatch }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID,
                    jobOID: payload.jobOID,
                    Note: payload.note
                }
            };
            return new Promise((resolve) => {
                API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/maintenance-job/note", params).then((res) => {
                    // Refresh the jobs list after adding a note
                    dispatch("getMaintenanceJobs", { 
                        clientName: payload.clientName,
                        contractorID: payload.contractorID
                    });
                    
                    if (res.status === "200") {
                        resolve({
                            success: true,
                            message: "Note added successfully"
                        });
                    } else {
                        resolve({
                            success: false,
                            message: "Failed to add note"
                        });
                    }
                }).catch(() => {
                    resolve({
                        success: false,
                        message: "Failed to add note"
                    });
                });
            });
        },
        
        getHistoricalStatements({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/historical-statements", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_HISTORICAL_STATEMENTS", res.data);
                    return res.data;
                }
                return [];
            });
        },
        
        getHistoricalStatementEntries({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID,
                    statementOID: payload.statementOID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/historical-statement/entries", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_CURRENT_HISTORICAL_STATEMENT", res.data);
                    return res.data;
                }
                return null;
            });
        },
        
        getPendingStatement({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/pending-statement", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_PENDING_STATEMENT", res.data);
                    return res.data;
                }
                return null;
            });
        },
        
        getSubmittedInvoices({ commit }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID
                }
            };
            return API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/submitted-invoices", params).then((res) => {
                if (res.status === "200") {
                    commit("SET_SUBMITTED_INVOICES", res.data);
                    return res.data;
                }
                return [];
            });
        },
        
        submitInvoice({ dispatch }, payload) {
            const params = {
                body: {
                    clientName: payload.clientName,
                    contractorID: payload.contractorID,
                    MaitenanceJobID: payload.maintenanceJobID,
                    PropertyID: payload.propertyID,
                    Amount: payload.amount,
                    Description: payload.description,
                    Documents: payload.documents
                }
            };
            return new Promise((resolve) => {
                API_SERVICE.post("peoplePortalRestAPI", "/accounts/contractor/submitted-invoices/submit", params).then((res) => {
                    // Refresh the submitted invoices list after adding a new one
                    dispatch("getSubmittedInvoices", { 
                        clientName: payload.clientName,
                        contractorID: payload.contractorID
                    });
                    
                    if (res.status === "200") {
                        resolve({
                            success: true,
                            message: "Invoice submitted successfully"
                        });
                    } else {
                        resolve({
                            success: false,
                            message: "Failed to submit invoice"
                        });
                    }
                }).catch(() => {
                    resolve({
                        success: false,
                        message: "Failed to submit invoice"
                    });
                });
            });
        },
        
        // Convenience method to load all contractor data at once
        loadContractorData({ dispatch }, payload) {
            return Promise.all([
                dispatch("getContractor", payload),
                dispatch("getContacts", payload),
                dispatch("getMaintenanceJobs", payload),
                dispatch("getHistoricalStatements", payload),
                dispatch("getPendingStatement", payload),
                dispatch("getSubmittedInvoices", payload)
            ]);
        }
    }
};
